import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useRegulatoryFrameworks,
  useRegulatoryWatches,
  useAddRegulatoryWatch,
  useDeleteRegulatoryWatch,
  type FrameworkRow,
} from '@/hooks/useRegulatoryQueries'

/**
 * Settings — Regulation watches.
 *
 * Renders the master framework list plus toggle controls for the user's watch
 * state. Frameworks with `coverage_status === 'full'` can be toggled; others
 * render a disabled "Pending coverage" button. The section heading is owned by
 * `SettingsPage` (which provides the `id="regulation-watches"` anchor used for
 * deep links from the regulatory dashboard tile).
 */
export function RegulationWatchesPanel(): JSX.Element {
  const frameworksQuery = useRegulatoryFrameworks()
  const watchesQuery = useRegulatoryWatches()
  const addMutation = useAddRegulatoryWatch()
  const deleteMutation = useDeleteRegulatoryWatch()

  const [pendingFrameworkId, setPendingFrameworkId] = useState<string | null>(
    null,
  )
  const [mutationError, setMutationError] = useState<string | null>(null)

  const watchedIds = useMemo(() => {
    const set = new Set<string>()
    for (const entry of watchesQuery.data?.watches ?? []) {
      set.add(entry.framework.id)
    }
    return set
  }, [watchesQuery.data])

  const sortedFrameworks: FrameworkRow[] = useMemo(() => {
    const arr = frameworksQuery.data?.frameworks ?? []
    return [...arr].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [frameworksQuery.data])

  if (frameworksQuery.isLoading || watchesQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Choose which regulatory frameworks personalise your weekly digest.
          Only frameworks with FULL coverage can be watched today.
        </p>
        <div className="flex flex-col gap-3" data-testid="watches-skeleton">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </div>
    )
  }

  if (frameworksQuery.isError) {
    return (
      <ErrorState
        title="Could not load frameworks"
        message="We could not load the regulatory framework list. Please try again."
        onRetry={() => frameworksQuery.refetch()}
      />
    )
  }

  const watchesUnavailable = watchesQuery.isError

  function handleToggle(framework: FrameworkRow, isWatched: boolean) {
    setMutationError(null)
    setPendingFrameworkId(framework.id)
    const onError = () => {
      setMutationError(
        'Could not update watch state. Please try again.',
      )
    }
    const onSettled = () => {
      setPendingFrameworkId((prev) =>
        prev === framework.id ? null : prev,
      )
    }
    if (isWatched) {
      deleteMutation.mutate(framework.id, {
        onError,
        onSettled,
      })
    } else {
      addMutation.mutate(framework.id, {
        onError,
        onSettled,
      })
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Choose which regulatory frameworks personalise your weekly digest. Only
        frameworks with FULL coverage can be watched today.
      </p>

      {watchesUnavailable ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          Could not load watch state — toggling is unavailable right now.
        </div>
      ) : null}

      {mutationError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {mutationError}
        </div>
      ) : null}

      {sortedFrameworks.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No regulatory frameworks are available yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {sortedFrameworks.map((framework) => {
            const isWatched = watchedIds.has(framework.id)
            const isFull = framework.coverageStatus === 'full'
            const rowPending = pendingFrameworkId === framework.id
            const disabled =
              watchesUnavailable || (!isWatched && !isFull) || rowPending

            let label: string
            let variant: 'default' | 'outline'
            if (rowPending) {
              label = 'Saving…'
              variant = isWatched ? 'default' : 'outline'
            } else if (isWatched) {
              label = 'Watching'
              variant = 'default'
            } else if (isFull) {
              label = 'Watch'
              variant = 'outline'
            } else {
              label = 'Pending coverage'
              variant = 'outline'
            }

            return (
              <li
                key={framework.id}
                className="flex items-start justify-between gap-3 rounded-md border border-border/60 bg-card px-3 py-2"
              >
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">
                    {framework.name}
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {framework.coverageStatus}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {framework.jurisdiction}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={variant}
                  disabled={disabled}
                  // R28: reveals which frameworks THIS user watches, in an attribute.
                  data-ph-no-capture
                  aria-label={
                    isWatched
                      ? `Stop watching ${framework.name}`
                      : `Watch ${framework.name}`
                  }
                  onClick={() => handleToggle(framework, isWatched)}
                >
                  {label}
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
